import { Input } from "./Field";

/**
 * An instant, entered as a local date and time.
 *
 * <p>The browser's {@code datetime-local} control has no timezone, which is exactly the trap here:
 * a release window is agreed with people in a place — "10 PM IST" — and the value stored has to be
 * an absolute instant or two readers in two countries will see two different times. So the control
 * edits wall-clock text, the caller supplies the zone that text is in, and the conversion happens
 * here rather than being left to whichever component touched it last.
 *
 * <p>Only {@code type="date"} existed in this application before releases; every other date it
 * stores is a business date with no time of day, and for those a plain date input is right.
 */
export function DateTimeField({
  value,
  onChange,
  timeZone,
  id,
  disabled,
}: {
  /** An ISO instant, or null. */
  value: string | null;
  onChange: (isoInstant: string | null) => void;
  /** The IANA zone the displayed wall-clock time is in. */
  timeZone: string;
  id?: string;
  disabled?: boolean;
}) {
  return (
    <Input
      id={id}
      type="datetime-local"
      disabled={disabled}
      value={toLocalInput(value, timeZone)}
      onChange={(event) =>
        onChange(event.target.value === "" ? null : toInstant(event.target.value, timeZone))
      }
    />
  );
}

/** An instant, rendered as the wall-clock time it corresponds to in the given zone. */
export function toLocalInput(iso: string | null, timeZone: string): string {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "00";
  // en-CA gives 24-hour time, but midnight can arrive as "24" in some engines.
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
}

/**
 * Wall-clock text in a named zone, as an absolute instant.
 *
 * <p>Works out the zone's offset at that moment rather than assuming a fixed one, so a window
 * booked either side of a daylight-saving change lands on the time people actually agreed.
 */
export function toInstant(local: string, timeZone: string): string | null {
  if (!local) {
    return null;
  }
  // Read the text as if it were UTC, then correct by the zone's offset at that instant.
  const asUtc = new Date(`${local}:00Z`);
  if (Number.isNaN(asUtc.getTime())) {
    return null;
  }
  const offsetMs = zoneOffsetMs(asUtc, timeZone);
  return new Date(asUtc.getTime() - offsetMs).toISOString();
}

/** How far ahead of UTC the zone is at a given instant, in milliseconds. */
function zoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(instant);

  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  const hour = get("hour") === 24 ? 0 : get("hour");
  const asIfUtc = Date.UTC(get("year"), get("month") - 1, get("day"), hour, get("minute"),
    get("second"));
  return asIfUtc - instant.getTime();
}
